/**
 * SEO component that works with Gatsby's Head API
 *
 * See: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */

import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import { useGlobalSEO, useSocialLinks } from "effects/useSiteContent"

// This is the component that will be used with Gatsby Head API
export function SEOHead({
  author,
  date,
  dateModified,
  description = "",
  lang = "en",
  meta = [],
  title,
  thumbnail = "",
  canonicalUrl,
  pageUrl,
  pageType = "website",
  structuredData = null,
}) {
  /** Social links from local YAML */
  const social = useSocialLinks()

  /** Global SEO defaults from local YAML */
  const globalSEO = useGlobalSEO()

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  const image =
    thumbnail !== false ? thumbnail || globalSEO.socialPreviewImage : false
  const metaDescription = description || globalSEO.defaultDescription
  const authorName = author || "Preset"
  const publishDate = date || new Date().toISOString()

  const pageTitleTemplate = globalSEO.siteTitle
    ? `%s | ${globalSEO.siteTitle}`
    : "%s"

  const formattedTitle = pageTitleTemplate.replace("%s", title)

  // Convert lang code to locale (e.g., "en" -> "en_US")
  const locale = lang === "en" ? "en_US" : lang.replace("-", "_")

  // Resource hints for better performance
  const resourceHints = [
    <link
      rel="preconnect"
      href="https://www.googletagmanager.com"
      key="preconnect-gtm"
    />,
    <link
      rel="preconnect"
      href="https://cdn.segment.com"
      key="preconnect-segment"
    />,
    <link
      rel="dns-prefetch"
      href="https://www.google-analytics.com"
      key="dns-prefetch-ga"
    />,
  ]

  const metaTags = [
    <html lang={lang} key="html-lang" />,
    <title key="title">{formattedTitle}</title>,
    ...resourceHints,
    <meta name="description" content={metaDescription} key="description" />,
    <meta property="og:title" content={title} key="og:title" />,
    <meta
      property="og:description"
      content={metaDescription}
      key="og:description"
    />,
    <meta property="og:type" content={pageType} key="og:type" />,
    <meta property="og:locale" content={locale} key="og:locale" />,
    <meta
      name="twitter:card"
      content={image ? "summary_large_image" : "summary"}
      key="twitter:card"
    />,
    <meta
      name="twitter:creator"
      content={social?.twitter?.handle}
      key="twitter:creator"
    />,
    <meta name="twitter:title" content={title} key="twitter:title" />,
    <meta
      name="twitter:description"
      content={metaDescription}
      key="twitter:description"
    />,
    <meta
      name="google-site-verification"
      content="w5Sbt7Iimra0OILk8vTwk4TAb1-l93NnkR3AP9TELOk"
      key="google-verification"
    />,
    <meta name="author" content={authorName} key="author" />,
  ]

  // Add og:url if pageUrl is provided
  if (pageUrl) {
    metaTags.push(<meta property="og:url" content={pageUrl} key="og:url" />)
  }

  // Add article-specific meta tags for blog posts
  if (pageType === "article") {
    if (date) {
      metaTags.push(
        <meta
          property="article:published_time"
          content={publishDate}
          key="article:published_time"
        />
      )
    }
    if (dateModified) {
      metaTags.push(
        <meta
          property="article:modified_time"
          content={dateModified}
          key="article:modified_time"
        />
      )
    }
    if (author) {
      metaTags.push(
        <meta property="article:author" content={author} key="article:author" />
      )
    }
  }

  const result = [...metaTags]

  if (canonicalUrl) {
    result.push(<link rel="canonical" href={canonicalUrl} key="canonical" />)
  }

  if (image) {
    let imageUrl
    if (image.startsWith("//")) {
      imageUrl = `https:${image}`
    } else if (image.startsWith("http")) {
      imageUrl = image
    } else {
      // For relative paths, preserve query parameters
      imageUrl = `${site.siteMetadata.siteUrl}${image}`
    }

    // Add image meta tags with dimensions for better social previews
    // Standard Open Graph image dimensions: 1200x630 (1.91:1 ratio)
    result.push(
      <meta property="og:image" content={imageUrl} key="og:image" />,
      <meta property="og:image:width" content="1200" key="og:image:width" />,
      <meta property="og:image:height" content="630" key="og:image:height" />,
      <meta name="twitter:image" content={imageUrl} key="twitter:image" />
    )

    // Dynamically determine image type from URL if possible
    const lowerUrl = imageUrl.toLowerCase()
    if (lowerUrl.endsWith(".png")) {
      result.push(
        <meta
          property="og:image:type"
          content="image/png"
          key="og:image:type"
        />
      )
    } else if (lowerUrl.endsWith(".jpg") || lowerUrl.endsWith(".jpeg")) {
      result.push(
        <meta
          property="og:image:type"
          content="image/jpeg"
          key="og:image:type"
        />
      )
    } else if (lowerUrl.endsWith(".webp")) {
      result.push(
        <meta
          property="og:image:type"
          content="image/webp"
          key="og:image:type"
        />
      )
    } else if (lowerUrl.endsWith(".gif")) {
      result.push(
        <meta
          property="og:image:type"
          content="image/gif"
          key="og:image:type"
        />
      )
    }
    // No type tag added if extension can't be determined
  }

  // Add any additional meta tags
  if (meta.length > 0) {
    meta.forEach((metaItem, index) => {
      const key = `custom-meta-${index}`
      if (metaItem.property) {
        result.push(
          <meta
            property={metaItem.property}
            content={metaItem.content}
            key={key}
          />
        )
      } else {
        result.push(
          <meta name={metaItem.name} content={metaItem.content} key={key} />
        )
      }
    })
  }

  // Add structured data (JSON-LD) if provided
  if (structuredData) {
    // Handle single schema or array of schemas
    const schemas = Array.isArray(structuredData)
      ? structuredData
      : [structuredData]

    schemas.forEach((schema, index) => {
      if (schema) {
        result.push(
          <script
            type="application/ld+json"
            key={`structured-data-${index}`}
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        )
      }
    })
  }

  return result
}

// Define prop types for SEOHead
SEOHead.propTypes = {
  author: PropTypes.string,
  date: PropTypes.string,
  dateModified: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.string,
      property: PropTypes.string,
    })
  ),
  title: PropTypes.string.isRequired,
  canonicalUrl: PropTypes.string,
  /** The full URL of the current page (for og:url) */
  pageUrl: PropTypes.string,
  /** Page type: "website" (default) or "article" for blog posts */
  pageType: PropTypes.oneOf(["website", "article"]),
  /** Set to thumbnail. Will use default thumbnail if unset. If set to `false` then it will not use the default image. */
  thumbnail: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Structured data (JSON-LD) - can be a single schema object or array of schema objects */
  structuredData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
}

// Export SEOHead as the default export
export default SEOHead
